import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ClassItem, ClassResponse, BookingResponse } from '../interfaces/class.interface';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root'
})
export class ClassService {
  constructor(
    private http: HttpClient,
    private apiService: ApiService
  ) {}

  private get apiUrl(): string {
    return this.apiService.getBaseApiUrl();
  }

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('auth_token');
    return new HttpHeaders()
      .set('Authorization', `Bearer ${token}`)
      .set('Content-Type', 'application/json')
      .set('Accept', 'application/json');
  }

  getClasses(tenantUuid: string, startDate: Date, endDate: Date): Observable<ClassResponse> {
    if (!tenantUuid) {
      return throwError(() => 'Tenant ID is required');
    }

    const formatDate = (date: Date): string => {
      return date.toISOString().split('T')[0];
    };

    const params = new HttpParams()
      .set('start_date', formatDate(startDate))
      .set('end_date', formatDate(endDate));

    return this.http.get<ClassResponse>(
      `${this.apiUrl}/open/tenants/${tenantUuid}/classes`,
      { params }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error loading classes:', error);
        if (error.status === 401) {
          return throwError(() => 'Please log in to view classes.');
        }
        return throwError(() => 'Failed to load classes. Please try again later.');
      })
    );
  }

  getClassById(tenantId: number, classId: number): Observable<ClassItem> {
    return this.http.get<ClassItem>(
      `${this.apiUrl}/tenants/${tenantId}/classes/${classId}`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error loading class details:', error);
        return throwError(() => 'Failed to load class details.');
      })
    );
  }

  joinClass(tenantUuid: string, classUuid: string, isWaitlist: boolean = false): Observable<BookingResponse> {
    const url = `${this.apiUrl}/open/tenants/${tenantUuid}/classes/${classUuid}/join_class`;
    const body = { 
      is_waitlist: isWaitlist
    };

    return this.http.post<BookingResponse>(
      url,
      body,
      { headers: this.getAuthHeaders() }
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Error joining class:', error);
        if (error.status === 401) {
          return throwError(() => [{
            errorCode: 'unauthorized',
            errorMessage: 'Please log in to book classes.',
            identifier: 'Auth'
          }]);
        }
        if (error.error && Array.isArray(error.error)) {
          return throwError(() => error.error);
        }
        return throwError(() => [{
          errorCode: 'unknown',
          errorMessage: 'Unable to complete booking. Please try again.',
          identifier: 'Unknown'
        }]);
      })
    );
  }
} 